import { nameOf } from "../../../../utils/functions/ReflectionUtil";
import {
  Design,
  DesignState,
  getDesignState,
} from "../../../../core/models/entities/Design";
import { formatDate } from "../../../../utils/functions/DateUtil";
import IconButton from "../../../components/LogoButton/IconButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faClose } from "@fortawesome/free-solid-svg-icons";
import React, { useRef, useState } from "react";
import { ColumnSettings } from "../../../components/GenericTable/SubComponents/ListFrame";
import { StrucCol } from "../../../components/StructureElements/StrucCol";
import { StrucRow } from "../../../components/StructureElements/StrucRow";
import { DesignPrices } from "../../../components/Logo/LogoPrices";
import { DesignStatusElement } from "../../../components/Design/DesignStatusElement";
import { DesignImage } from "../../../components/Design/DesignImage";
import { Verdict } from "../../../components/ApprovalVoting/approval-voting";
import { Tooltip } from "@mui/material";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { useCartHidden } from "../../../../hooks/useCartHidden";
import { Role } from "../../../../core/models/entities/Role";
import { DesignClickCallback } from "../vmHooks/useFunctionalListDesignViewModel";
import {
  AddToCartIcon,
  EditIcon,
  NoteIcon,
  TrashcanCrossIcon,
} from "../../../../resources/icons/Icons";
import { Stepper } from "../../../components/Buttons/Stepper";
import { ApprovalStatus } from "../../../../core/models/entities/Approval";
import CustomerTooltip from "../../../components/Organization/CustomerTooltip";
import {
  getDesignMinQuantity,
  isValidDesignQuantity,
} from "../../../../utils/functions/QuantityUtil";
import { hasUserPendingApprovalStatus } from "../../../../core/models/entities/BaseLogo";
import { usePriceVisibility } from "../../../../contexts/PriceVisibilityContext";
import { CircularProgress } from "@mui/material";
import { ConfirmationGuard } from "../../../components/ConfirmationGuard";
import { CopyNumberButton } from "../../../components/CopyToClipboard/copy-number-button.component";

interface DesignRowItem {
  design: Design;
  columns: ColumnSettings;
  onApprove: DesignClickCallback;
  onReject: DesignClickCallback;
  onPlaceVerdict: (design: Design, verdict: Verdict) => void;
  onSeeRejectNote: (design: Design) => void;
  onAddToCart: (design: Design, quantity: number) => void;
  onEditDesign: (design: Design) => void;
  onDeleteDesign: (design: Design) => void;
  onInitiateApproval: (design: Design) => void;
  onRejectAndDelete: (design: Design, note?: string) => Promise<void>;
  updatingDesignId?: number;
  loadingPrices?: boolean;
  // Folder drag-and-drop (vendor/admin only): when set, the row becomes an
  // HTML5 drag source; the callback fills the dataTransfer payload (see
  // folder-bar.component.tsx for the drop-target side).
  onDragStartDesign?: (
    design: Design,
    e: React.DragEvent<HTMLDivElement>,
  ) => void;
}

// TODO Deprecate
export function isDesignApprovedByMe(design: Design, role: Role) {
  switch (role) {
    case Role.Admin:
      return false;
    case Role.Vendor:
      // Return true only if vendor has explicitly approved
      return design.approval?.vendor?.status === ApprovalStatus.Approved;
    case Role.EndUser:
      // Return true only if customer has explicitly approved
      return design.approval?.customer?.status === ApprovalStatus.Approved;
  }
}

export function DesignRowItem({
  design,
  columns,
  onDeleteDesign,
  onEditDesign,
  onAddToCart,
  onReject,
  onApprove,
  updatingDesignId,
  loadingPrices,
  onDragStartDesign,
}: DesignRowItem) {
  const { role } = useProfileContext();
  const { hidePrices, showCustomerPrices } = usePriceVisibility();
  const cartHidden = useCartHidden();

  // Check if design has customer prices
  const hasCustomerPrices =
    design.product?.totalPrices?.some(
      (p: any) =>
        p.adjustedCustomerPrice !== null &&
        p.adjustedCustomerPrice !== undefined,
    ) ?? false;

  // Determine if prices should be shown
  const shouldShowPrices =
    !hidePrices && (!showCustomerPrices || hasCustomerPrices);

  const minQuantity = getDesignMinQuantity(design);
  const [quantityForOrder, setQuantityForOrder] = useState<number>(minQuantity);
  function SeeRejectNote(e: React.MouseEvent<HTMLElement>) {}

  function EditLogo(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation();
    onEditDesign(design);
  }

  function AddToCart(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation();
    onAddToCart(design, quantityForOrder);
  }

  function onDeleteClick(e?: React.MouseEvent<HTMLElement>) {
    e?.stopPropagation();
    onDeleteDesign(design);
  }

  function handleApproveClick(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation();
    onApprove(design);
  }

  function handleRejectClick(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation();
    onReject(design);
  }

  const overallState = getDesignState(design);

  const isUpdating =
    updatingDesignId === design.id && overallState === DesignState.Draft;

  function draftActions() {
    return (
      <>
        {design.isEditable !== false && (
          <IconButton
            key={"approve"}
            onClick={EditLogo}
            icon={<EditIcon />}
            variant="success"
          >
            Edit
          </IconButton>
        )}
        <ConfirmationGuard
          anchor={
            <IconButton icon={<TrashcanCrossIcon />} variant="error">
              Delete
            </IconButton>
          }
          text={"Do you want to Delete this design?"}
          onAccept={onDeleteClick}
        />
      </>
    );
  }

  function rejectedActions() {
    return (
      <>
        <IconButton
          key={"see-note"}
          onClick={SeeRejectNote}
          icon={<NoteIcon />}
        >
          See Note
        </IconButton>
        {design.isEditable !== false && (
          <IconButton
            key={"approve"}
            onClick={EditLogo}
            icon={<EditIcon />}
            variant="success"
          >
            Edit
          </IconButton>
        )}
        <ConfirmationGuard
          anchor={
            <IconButton icon={<TrashcanCrossIcon />} variant="error">
              Delete
            </IconButton>
          }
          text={"Do you want to Delete this design?"}
          onAccept={onDeleteClick}
        />
      </>
    );
  }

  function pendingAction() {
    if (role == undefined || role == null) {
      return;
    }

    return (
      <>
        <IconButton
          onClick={handleApproveClick}
          icon={<FontAwesomeIcon icon={faCheck} />}
          variant="success"
        >
          Approve
        </IconButton>
        <IconButton
          onClick={handleRejectClick}
          icon={<FontAwesomeIcon icon={faClose} />}
          variant="error"
        >
          Reject
        </IconButton>
      </>
    );
  }

  function doneActions() {
    const minQuantity = getDesignMinQuantity(design);
    if (role === Role.Admin) {
      return <></>;
    }

    if (cartHidden) {
      return <></>;
    }

    return (
      <>
        <Stepper
          min={1}
          value={quantityForOrder}
          onValueChange={setQuantityForOrder}
          className="h-7"
        />
        <IconButton
          key={"add-order"}
          disabled={
            !isValidDesignQuantity(design, quantityForOrder) || loadingPrices
          }
          onClick={AddToCart}
          icon={<AddToCartIcon />}
          variant="success"
        >
          {loadingPrices ? "Loading..." : "Add to Order"}
        </IconButton>
      </>
    );
  }

  function getActions(state: DesignState) {
    // Check if current user has pending approval status
    const userHasPendingApproval = hasUserPendingApprovalStatus(
      design.approval,
      role as number,
    );

    // If user has pending approval status, show pending actions regardless of design state
    if (userHasPendingApproval) {
      return pendingAction();
    }

    if (overallState === DesignState.Approval && role === Role.Admin) {
      return draftActions();
    }

    switch (state) {
      case DesignState.Draft:
        return draftActions();
      case DesignState.Rejected:
      case DesignState.Pending:
        return rejectedActions();
      case DesignState.Approval:
        return doneActions();
      case DesignState.Done:
        return doneActions();
    }
  }

  const actions = getActions(overallState);

  // True while (and briefly after) an HTML5 drag: suppresses the row-click
  // navigation that lives on the surrounding ListItemFrame. Browsers do not
  // normally fire click after a drag, but this guards the edge cases.
  const isDragging = useRef(false);

  const dragProps = onDragStartDesign
    ? {
        draggable: true,
        onDragStart: (e: React.DragEvent<HTMLDivElement>) => {
          isDragging.current = true;
          onDragStartDesign(design, e);
        },
        onDragEnd: () => {
          setTimeout(() => {
            isDragging.current = false;
          }, 150);
        },
        onClickCapture: (e: React.MouseEvent<HTMLDivElement>) => {
          if (isDragging.current) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
      }
    : {};

  return (
    <StrucRow className={"h-[100px] items-start py-4"} {...dragProps}>
      <StrucCol
        size={columns[nameOf<Design>((x) => x.displayImage)].weight}
        className={"pr-3 rounded-md"}
      >
        {isUpdating ? (
          <div className="bg-white w-full h-[74px] flex items-center justify-center rounded-md border border-gray-100">
            <CircularProgress size={14} />
          </div>
        ) : (
          <div className="bg-white w-full max-h-full flex items-center justify-center rounded-md py-1">
            <DesignImage
              design={design}
              className={
                "object-contain bg-white hover:rounded hover:border max-h-[74px] origin-left hover:scale-[3] hover:ease-out transition-all duration-150 ease-out" +
                (design.isEditable ? " hover:p-1" : "")
              }
            />
          </div>
        )}
      </StrucCol>
      <StrucCol size={columns["style"].weight}>
        <div className="flex items-center">
          <Tooltip title={design.product?.mul ?? "-"} placement={"top-start"}>
            <span
              className={
                "font-bold whitespace-nowrap overflow-x-hidden overflow-ellipsis min-w-0"
              }
              onClick={(e) => e.stopPropagation()}
            >
              {design.product?.mul ?? "-"}
            </span>
          </Tooltip>
          {design.product?.mul && (
            <CopyNumberButton value={design.product.mul} className="ml-1" />
          )}
        </div>
        <p>
          {design.style?.manufacturer?.name || design.navManufacturerName}{" "}
          <br />
          {design.style?.model || design.navStyleName}
        </p>
      </StrucCol>
      <StrucCol size={columns[nameOf<Design>((x) => x.title)].weight}>
        <Tooltip title={design.title} placement={"top-start"}>
          <span
            className={
              "font-bold whitespace-nowrap overflow-x-hidden overflow-ellipsis"
            }
          >
            {design.title}
          </span>
        </Tooltip>
      </StrucCol>
      {/* Vendor column (only shown for Admin users) */}
      {columns["vendor"] && (
        <StrucCol size={columns["vendor"].weight}>
          {design.vendor && <CustomerTooltip customer={design.vendor} />}
        </StrucCol>
      )}
      <StrucCol size={columns[nameOf<Design>((x) => x.customer)].weight}>
        {design.customer?.type !== "vendor" && (
          <CustomerTooltip customer={design.customer} />
        )}
      </StrucCol>
      {/* <StrucCol size={columns["priceSort"].weight}>
        {columns["priceSort"].displayValue && shouldShowPrices ? (
          loadingPrices ? (
            <div className="animate-pulse space-y-1">
              <div className="h-3 bg-gray-300 rounded w-20"></div>
              <div className="h-3 bg-gray-300 rounded w-20"></div>
              <div className="h-3 bg-gray-300 rounded w-20"></div>
              <div className="h-3 bg-gray-300 rounded w-20"></div>
            </div>
          ) : (
            <DesignPrices design={design} />
          )
        ) : (
          <span></span>
        )}
      </StrucCol> */}
      <StrucCol size={columns[nameOf<Design>((x) => x.createdAtUtc)].weight}>
        {formatDate(design.createdAtUtc)}
      </StrucCol>
      <StrucCol size={columns[nameOf<Design>((x) => x.status)].weight}>
        <DesignStatusElement design={design} />
      </StrucCol>
      <StrucCol
        size={columns["action"].weight}
        className={"items-start !justify-around space-y-2 h-full"}
      >
        {actions}
      </StrucCol>
    </StrucRow>
  );
}
